import "twin.macro";

import { useAuth0 } from "@auth0/auth0-react";
import { jwtDecode, JwtPayload } from "jwt-decode";
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";

import { Permission } from "./utils/auth/Permission";

interface DecodedJwtPayload extends JwtPayload {
  permissions?: Permission[];
}

interface PermissionsContextType {
  // isLoading: boolean;
  permissions: Permission[];
  hasPermission: (permission: Permission) => boolean;
}

const PermissionsContext = createContext<PermissionsContextType | null>(null);

const PermissionsProvider = (props: { children: ReactNode }) => {
  const { getAccessTokenSilently } = useAuth0();

  const [permissions, setPermissions] = useState<Permission[]>([]);

  useEffect(() => {
    const extractPermissions = async (): Promise<void> => {
      try {
        const token = await getAccessTokenSilently({ cacheMode: "cache-only" });
        const decoded = jwtDecode<DecodedJwtPayload>(token);
        setPermissions(decoded.permissions ?? []);
      } catch (error) {
        console.warn("Unable to get access token", error);
      }
    };

    void extractPermissions();
  }, [getAccessTokenSilently]);

  const hasPermission = (permission: Permission) =>
    permissions.includes(permission);

  return (
    <PermissionsContext.Provider
      value={{ permissions, hasPermission }}
      {...props}
    />
  );
};

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (context === null) {
    throw new Error(
      "usePermissions has to be used within <PermissionsProvider />"
    );
  }
  return context;
};

export default PermissionsProvider;
